/**
 * /payroll-l4/submissions — Submission Log (Step 12).
 *
 * Cross-project list of every WH-347 submission with portal response.
 * Lets the admin record the response (accepted / rejected + detail) when
 * it comes back from the portal — that mirrors onto the linked report.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { api, errorMessage } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { useToast } from "@/components/Toaster";
import type {
  SubmissionMethod,
  SubmissionResponse,
  WH347Submission,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/payroll-l4/submissions")({
  component: SubmissionLogPage,
});

const RESPONSE_COLOR: Record<SubmissionResponse, "default" | "warning" | "success" | "error"> = {
  pending: "warning",
  accepted: "success",
  rejected: "error",
};

function SubmissionLogPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [methodFilter, setMethodFilter] = useState<SubmissionMethod | "">("");
  const [statusFilter, setStatusFilter] = useState<SubmissionResponse | "">("");
  const [editing, setEditing] = useState<WH347Submission | null>(null);

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["payroll-l4", "submissions", methodFilter, statusFilter],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (methodFilter) qs.set("method", methodFilter);
      if (statusFilter) qs.set("response_status", statusFilter);
      return (await api(
        `/api/payroll-l4/submissions${qs.toString() ? "?" + qs : ""}`,
      )) as WH347Submission[];
    },
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Submission Log</Typography>
          <Typography variant="body2" color="text.secondary">
            Track each WH-347 you've sent to LCPtracker, the CA DIR portal, or
            eMars, and record the agency's response (accepted / rejected).
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint
              docPath="payroll-l4/step-7-14-lifecycle-ai.md#submission-tracker-step-12"
              label="Submission tracking help"
            />
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <TextField
            select size="small" label="Method"
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value as SubmissionMethod | "")}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="lcptracker">LCPtracker</MenuItem>
            <MenuItem value="dir_ecpr">CA DIR eCPR</MenuItem>
            <MenuItem value="emars">eMars</MenuItem>
            <MenuItem value="pdf_email">PDF email</MenuItem>
            <MenuItem value="manual_portal">Manual portal</MenuItem>
          </TextField>
          <TextField
            select size="small" label="Response"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SubmissionResponse | "")}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(error)}</Alert>}

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Submitted</TableCell>
                <TableCell>Report</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Submitted to</TableCell>
                <TableCell>Confirmation</TableCell>
                <TableCell>Response</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.submitted_at?.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell>
                    {s.report_payroll_number != null ? (
                      <>
                        #{s.report_payroll_number}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          week {s.report_week_ending}
                        </Typography>
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.submission_method} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{s.submitted_to ?? "—"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {s.confirmation_reference ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={s.response_status} color={RESPONSE_COLOR[s.response_status]} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240, fontSize: 11 }}>
                    {s.response_detail ?? <span style={{ color: "#999" }}>{s.notes}</span>}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        title="Update response"
                        aria-label="Update response"
                        onClick={() => setEditing(s)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Open report"
                        aria-label="Open report"
                        component={Link}
                        to={"/payroll-l4/certified-payroll-reports/$reportId" as never}
                        params={{ reportId: s.report_id } as never}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {methodFilter || statusFilter
                        ? "No submissions match this filter."
                        : "No submissions yet. After you sign a WH-347 report, export and submit it, then log the result here."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <UpdateResponseDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["payroll-l4", "submissions"] });
          qc.invalidateQueries({ queryKey: ["payroll-l4", "wh347"] });
          toast.success("Submission response updated.");
        }}
      />
    </Box>
  );
}


function UpdateResponseDialog({
  target, onClose, onSaved,
}: {
  target: WH347Submission | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [responseStatus, setResponseStatus] = useState<SubmissionResponse>("accepted");
  const [detail, setDetail] = useState("");

  // Hydrate from the target whenever it changes (open/close)
  if (target && responseStatus !== target.response_status && responseStatus === "accepted") {
    // First open with this target — pull in current values
    if (target.response_status !== "pending") {
      setResponseStatus(target.response_status);
    }
    if (target.response_detail) setDetail(target.response_detail);
  }

  const save = useMutation({
    mutationFn: async () =>
      await api(
        `/api/payroll-l4/submissions/${target!.id}/response`,
        {
          method: "PATCH",
          body: {
            response_status: responseStatus,
            response_detail: detail.trim() || null,
          },
        },
      ),
    onSuccess: () => {
      setDetail("");
      onSaved();
    },
  });

  return (
    <Dialog
      open={!!target}
      onClose={() => !save.isPending && (setDetail(""), onClose())}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Update submission response</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Recording an accepted / rejected response also flips the
          underlying report's status to match. Use this when the portal
          (or recipient) returns its decision.
        </Typography>
        <Stack spacing={2}>
          <TextField
            select fullWidth size="small" label="Response"
            value={responseStatus}
            onChange={(e) => setResponseStatus(e.target.value as SubmissionResponse)}
            disabled={save.isPending}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField
            fullWidth multiline rows={3} size="small" label="Detail"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            disabled={save.isPending}
            placeholder="ack code / rejection reason / portal response text"
          />
        </Stack>
        {save.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(save.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setDetail(""); onClose(); }} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
